import { useCallback, useEffect, useState } from 'react';
import {
  Play, Pencil, Trash2, ChevronDown, ChevronRight, Plus, AlertTriangle,
} from 'lucide-react';
import { api, type CronJob, type RunLog } from '../lib/api';
import { cronText, naechsterLauf, laufText, relativ, uhrzeit } from '../lib/format';
import { JobDialog } from '../components/JobDialog';

/**
 * Die wichtigste Seite der App — hier entsteht alles, was automatisch laedt.
 *
 * Gegenueber der alten Fassung geaendert:
 *  • Zeitplan im Klartext statt `0 *​/6 * * *`, dazu der naechste Termin.
 *  • Die Lauf-Historie (`/cron-jobs/:id/runs`) wird angezeigt. Sie existierte
 *    im Backend von Anfang an und wurde nie abgerufen — dabei ist "was hat der
 *    Job eigentlich getan" die einzige Frage, die man hier wirklich hat.
 *  • Die Qualitaetsregeln stehen sichtbar in der Zeile statt hinter einem
 *    Aufklapp-Pfeil. Genau sie entscheiden, ob etwas doppelt geladen wird.
 *  • Loeschen ist vom "Jetzt ausfuehren" getrennt und fragt nach.
 */
export function CronJobs() {
  const [jobs, setJobs] = useState<CronJob[] | null>(null);
  const [offen, setOffen] = useState<number | null>(null);
  const [laeuft, setLaeuft] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<CronJob | 'neu' | null>(null);
  const [loeschen, setLoeschen] = useState<CronJob | null>(null);

  const laden = useCallback(async () => {
    try {
      setJobs(await api<CronJob[]>('/cron-jobs'));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  async function jetztLaufen(job: CronJob) {
    setLaeuft(job.id);
    try {
      await api(`/cron-jobs/${job.id}/run-now`, { method: 'POST' });
      await laden();
      setOffen(job.id); // Ergebnis gleich zeigen, statt es suchen zu lassen
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  async function umschalten(job: CronJob) {
    // Sofort umschalten, damit sich der Schalter nicht traege anfuehlt;
    // bei Fehlschlag holt `laden()` den echten Stand zurueck.
    setJobs((j) => j?.map((x) => (x.id === job.id ? { ...x, is_enabled: !x.is_enabled } : x)) ?? null);
    try {
      await api(`/cron-jobs/${job.id}/toggle`, { method: 'POST' });
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
    await laden();
  }

  if (fehler && !jobs) return <div className="hinweis hinweis--schlecht">{fehler}</div>;
  if (!jobs) return <div className="laedt">Lade …</div>;

  return (
    <>
      <div className="kopf">
        <div>
          <h1>Automatik</h1>
          <p>Was hier steht, sucht und lädt von selbst. {jobs.filter((j) => j.is_enabled).length} von {jobs.length} aktiv.</p>
        </div>
        <button className="knopf knopf--haupt" onClick={() => setDialog('neu')}>
          <Plus size={16} /> Neuer Auftrag
        </button>
      </div>

      {fehler && <div className="hinweis hinweis--schlecht" style={{ marginBottom: 14 }}>{fehler}</div>}

      {jobs.length === 0 ? (
        <div className="karte karte__leer">
          Noch kein Auftrag. Über <strong>Neuer Auftrag</strong> oder direkt aus einem Suchtreffer anlegen.
        </div>
      ) : (
        <div className="stapel">
          {jobs.map((job) => (
            <JobZeile
              key={job.id}
              job={job}
              offen={offen === job.id}
              laeuft={laeuft === job.id}
              aufKlappen={() => setOffen(offen === job.id ? null : job.id)}
              aufLaufen={() => jetztLaufen(job)}
              aufSchalten={() => umschalten(job)}
              aufBearbeiten={() => setDialog(job)}
              aufLoeschen={() => setLoeschen(job)}
            />
          ))}
        </div>
      )}

      {dialog && (
        <JobDialog
          job={dialog === 'neu' ? null : dialog}
          aufSchliessen={() => setDialog(null)}
          aufFertig={async () => { setDialog(null); await laden(); }}
        />
      )}

      {loeschen && (
        <div className="overlay" onClick={() => setLoeschen(null)}>
          <div className="karte dialog" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="karte__kopf">
              <span className="karte__titel"><AlertTriangle size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Auftrag löschen?</span>
            </div>
            <div className="dialog__leib">
              <p style={{ marginTop: 0 }}>
                <strong>{loeschen.name}</strong> wird gelöscht und sucht nichts mehr.
              </p>
              <p className="leise klein" style={{ marginBottom: 0 }}>
                Bereits geladene Dateien bleiben liegen — gelöscht wird nur der Auftrag.
              </p>
            </div>
            <div className="dialog__fuss">
              <button className="knopf" onClick={() => setLoeschen(null)}>Abbrechen</button>
              <button
                className="knopf knopf--gefahr"
                onClick={async () => {
                  try {
                    await api(`/cron-jobs/${loeschen.id}`, { method: 'DELETE' });
                  } catch (e) {
                    setFehler(e instanceof Error ? e.message : String(e));
                  }
                  setLoeschen(null);
                  await laden();
                }}
              >
                <Trash2 size={15} /> Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function JobZeile({
  job, offen, laeuft, aufKlappen, aufLaufen, aufSchalten, aufBearbeiten, aufLoeschen,
}: {
  job: CronJob; offen: boolean; laeuft: boolean;
  aufKlappen: () => void; aufLaufen: () => void; aufSchalten: () => void;
  aufBearbeiten: () => void; aufLoeschen: () => void;
}) {
  const naechster = job.is_enabled ? naechsterLauf(job.cron_expression) : null;

  return (
    <div className="karte">
      <div className="karte__kopf" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="reihe" style={{ marginBottom: 5 }}>
            <button
              className="knopf knopf--klein knopf--nur-icon"
              onClick={aufKlappen}
              aria-label={offen ? 'Zuklappen' : 'Aufklappen'}
            >
              {offen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
            <span className="karte__titel" style={{ fontSize: 15 }}>{job.name}</span>
            {!job.is_enabled && <span className="chip chip--neutral">pausiert</span>}
            {job.auto_download
              ? <span className="chip chip--gut">lädt selbst</span>
              : <span className="chip chip--warn">nur suchen</span>}
          </div>

          {/* Zeitplan im Klartext — der Kern der Verbesserung gegenueber
              der alten Zeile, die hier rohes Cron zeigte. */}
          <div className="klein leise" style={{ marginLeft: 36 }}>
            {cronText(job.cron_expression)}
            {naechster && <> · nächster {relativ(naechster.toISOString())}</>}
            {' · zuletzt '}{relativ(job.last_run_at)}
          </div>

          <div className="reihe" style={{ marginLeft: 36, marginTop: 8, flexWrap: 'wrap', gap: 6 }}>
            <span className="chip chip--akzent mono">{job.search_query}</span>
            {job.search_type === 'tv' && job.target_season != null && (
              <span className="chip chip--neutral">Staffel {job.target_season}</span>
            )}
            {job.preferred_resolution && (
              <span className="chip chip--neutral">ab {job.preferred_resolution}</span>
            )}
            {job.quality_cutoff
              ? <span className="chip chip--neutral">aufwerten bis {job.quality_cutoff}</span>
              : <span className="chip chip--neutral" title="Ohne Grenze wird eine vorhandene Folge nie durch eine bessere ersetzt.">keine Aufwertung</span>}
            {job.upgrade_propers && <span className="chip chip--neutral">PROPER/REPACK</span>}
          </div>
        </div>

        <div className="reihe" style={{ flex: '0 0 auto' }}>
          <button
            className="schalter"
            data-an={job.is_enabled}
            onClick={aufSchalten}
            aria-label={job.is_enabled ? 'Pausieren' : 'Aktivieren'}
            title={job.is_enabled ? 'Pausieren' : 'Aktivieren'}
          ><i /></button>
          <button className="knopf knopf--klein" onClick={aufLaufen} disabled={laeuft}>
            <Play size={14} /> {laeuft ? 'läuft …' : 'Jetzt'}
          </button>
          <button className="knopf knopf--klein knopf--nur-icon" onClick={aufBearbeiten} title="Bearbeiten">
            <Pencil size={15} />
          </button>
          <button className="knopf knopf--klein knopf--nur-icon knopf--gefahr" onClick={aufLoeschen} title="Löschen">
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {offen && <Laeufe jobId={job.id} />}
    </div>
  );
}

/** Lauf-Historie. Nutzt `/cron-jobs/:id/runs` — ein Endpunkt, den es seit
 *  jeher gibt und den die alte Oberflaeche nie aufgerufen hat. */
function Laeufe({ jobId }: { jobId: number }) {
  const [logs, setLogs] = useState<RunLog[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let weg = false;
    api<RunLog[]>(`/cron-jobs/${jobId}/runs?limit=12`)
      .then((d) => { if (!weg) setLogs(d); })
      .catch((e) => { if (!weg) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { weg = true; };
  }, [jobId]);

  if (fehler) return <div className="dialog__leib"><div className="hinweis hinweis--schlecht">{fehler}</div></div>;
  if (!logs) return <div className="laedt">Lade Läufe …</div>;
  if (!logs.length) return <div className="karte__leer">Noch nie gelaufen.</div>;

  return (
    <table className="tabelle">
      <thead>
        <tr>
          <th style={{ width: 150 }}>Zeitpunkt</th>
          <th style={{ width: 110 }}>Ergebnis</th>
          <th>Was passiert ist</th>
          <th className="num" style={{ width: 90 }}>Treffer</th>
        </tr>
      </thead>
      <tbody>
        {logs.map((l) => (
          <tr key={l.id}>
            <td className="klein leise" title={uhrzeit(l.started_at)}>{relativ(l.started_at)}</td>
            <td>
              <span className={
                l.status === 'error' ? 'chip chip--schlecht'
                : l.downloads_pushed > 0 ? 'chip chip--gut'
                : 'chip chip--neutral'
              }>
                {l.status === 'error' ? 'Fehler'
                  : l.downloads_pushed > 0 ? `${l.downloads_pushed} geladen`
                  : 'nichts Neues'}
              </span>
            </td>
            <td className="klein leise">{laufText(l)}</td>
            <td className="num klein leise">{l.results_found}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
